import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useFields } from './fields';
import { ContentCustom, CreateUpdateContainer } from '../../components';

import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';
import { useConfig } from './utils/createUpdateConfig';
import { FormExtra } from './form/FormExtra';
import { useUnlockEditProfile } from '../../utils/useUnlockEditProfile';
import { useHandleProfileLock } from '../../utils/useHandleProfileLock';

/**
 * CreateUpdatePensioner component for creating or updating pensioner information.
 * @module CreateUpdatePensioner
 * @param {Object} props - Component props.
 * @param {string} props.purpose - Purpose of the component (e.g., 'create' or 'update').
 * @returns {JSX.Element} React component.
 */
export const CreateUpdatePensioner = ({ purpose }) => {
  const [filesList, setFilesList] = useState({
    photo: [],
    secondary_profile: { secondary_profile_identification_photo: [] }
  });
  const [isChildren, setIsChildren] = useState(false);
  const [isGrandChildren, setIsGrandChildren] = useState(false);
  const [removedFiles, setRemovedFiles] = useState([]);
  const [showSecondProfile, setShowSecondProfile] = useState(false);
  const [internetAccess, setInternetAccess] = useState(true);
  const [country, setCountry] = useState('');

  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();
  const { id } = useParams();
  const { unlockEditProfile } = useUnlockEditProfile(id, 'pensioners');
  const [form] = Form.useForm();
  const { handleProfileLock } = useHandleProfileLock(id);
  const {
    personnalInformationsFields,
    additionalInformationsFields,
    extraInformationsFields,
    isFieldsLoading
  } = useFields({
    filesList,
    setFilesList,
    setShowSecondProfile,
    showSecondProfile,
    form,
    setInternetAccess,
    internetAccess,
    isChildren,
    isGrandChildren,
    setIsChildren,
    setIsGrandChildren,
    setRemovedFiles,
    country,
    setCountry
  });
  const config = useConfig({
    filesList,
    setFilesList,
    dispatchAPI,
    message,
    setInternetAccess,
    setShowSecondProfile,
    setIsChildren,
    setIsGrandChildren,
    form,
    removedFiles,
    setCountry
  });

  /**
   * Effect hook to call the unlock function before closing the tab and clean up on unmount.
   * @listens beforeunload
   */
  useEffect(() => {
    if (purpose === 'edit') {
      (async () => {
        await handleProfileLock();
      })();

      const handleBeforeUnload = () => {
        unlockEditProfile();
      };

      globalThis.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        // clean up the event handler when the component unmounts
        globalThis.removeEventListener('beforeunload', handleBeforeUnload);
        // call the unlock func when the component unmounts
        unlockEditProfile();
      };
    }
    return undefined;
  }, [purpose, handleProfileLock, unlockEditProfile]);

  return (
    <ContentCustom transparent style={{ margin: 0 }}>
      <CreateUpdateContainer
        verticalLayout
        navSections={[
          {
            key: 'personal_informations',
            icon: 'user',
            fields: personnalInformationsFields
          },
          {
            key: 'additional_informations',
            icon: 'home',
            fields: additionalInformationsFields
          },
          {
            key: 'extra_informations',
            icon: 'profile',
            fields: extraInformationsFields
          }
        ].map((section) => ({
          ...section,
          title: t(`pensioners.form.sub_title.${section.key}`),
          selector: `#section-${section.key}`
        }))}
        withPageHeaderCustom
        loadingFields={isFieldsLoading}
        purpose={purpose}
        baseUrl="pensioners/form/backoffice"
        resource="pensioners"
        populate="photo"
        config={config}
        customFormInstance={form}
        formExtra={
          <FormExtra
            personnalInformationsFields={personnalInformationsFields}
            additionalInformationsFields={additionalInformationsFields}
            extraInformationsFields={extraInformationsFields}
          />
        }
      />
    </ContentCustom>
  );
};

CreateUpdatePensioner.propTypes = {
  purpose: PropTypes.string.isRequired
};
